import { Box, Typography, alpha } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useBinanceTickers, TOP_SYMBOLS, fmtPrice } from '../../hooks/useMarketData';

/** Horizontal strip of live prices for the top symbols. */
export function MarketTicker({
  symbols = TOP_SYMBOLS,
  selected,
  onSelect,
}: {
  symbols?: string[];
  selected?: string;
  onSelect?: (symbol: string) => void;
}) {
  const tickers = useBinanceTickers(symbols);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { height: 6 } }}>
      {symbols.map((sym) => {
        const t = tickers[sym];
        const up = (t?.changePct ?? 0) >= 0;
        const color = up ? '#16c784' : '#ea3943';
        const active = selected === sym;
        return (
          <Box
            key={sym}
            onClick={() => onSelect?.(sym)}
            sx={{
              flex: '0 0 auto',
              minWidth: 150,
              px: 2,
              py: 1.25,
              borderRadius: 2.5,
              cursor: onSelect ? 'pointer' : 'default',
              border: (th) => `1px solid ${active ? alpha(th.palette.primary.main, 0.6) : 'rgba(255,255,255,0.07)'}`,
              backgroundColor: (th) => (active ? alpha(th.palette.primary.main, 0.12) : alpha('#0f1628', 0.6)),
              transition: 'border-color 140ms ease, background-color 140ms ease',
              '&:hover': { borderColor: (th) => alpha(th.palette.primary.main, 0.4) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={`https://assets.coincap.io/assets/icons/${sym.replace('USDT', '').toLowerCase()}@2x.png`}
                alt=""
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0 }}
              />
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                {sym.replace('USDT', '')}<Typography component="span" sx={{ color: 'text.secondary', fontSize: 11 }}>/USDT</Typography>
              </Typography>
            </Box>
            <Typography className="mono" sx={{ fontWeight: 700, fontSize: 15, mt: 0.25 }}>
              {fmtPrice(t?.price)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', color, fontSize: 12, fontWeight: 600 }}>
              {up ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
              {t ? `${up ? '+' : ''}${t.changePct.toFixed(2)}%` : '—'}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
